"use client";

import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage, type Messaging } from "firebase/messaging";

import { getFirebaseVapidKey, getFirebaseWebConfig, isWebPushConfigured } from "@/lib/firebase/config";

const SW_URL = "/firebase-messaging-sw.js";
const TOKEN_STORAGE_KEY = "techxeed.webPush.fcmToken";

/** Dispatched from a real user gesture (e.g. notification bell) to allow Safari permission prompt. */
export const WEB_PUSH_PROMPT_EVENT = "techxeed:web-push-prompt";

let messagingInstance: Messaging | null = null;

function getOrInitFirebaseApp(): FirebaseApp | null {
	const config = getFirebaseWebConfig();
	if (!config) return null;

	return getApps().length > 0 ? getApps()[0]! : initializeApp(config);
}

async function getMessagingInstance(): Promise<Messaging | null> {
	if (!(await isSupported())) return null;
	if (messagingInstance) return messagingInstance;

	const app = getOrInitFirebaseApp();
	if (!app) return null;

	messagingInstance = getMessaging(app);
	return messagingInstance;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
	if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
		return null;
	}

	try {
		const existing = await navigator.serviceWorker.getRegistration("/");
		if (existing?.active) return existing;

		return await navigator.serviceWorker.register(SW_URL, { scope: "/" });
	} catch {
		return null;
	}
}

export type WebPushRegistrationResult = {
	token: string | null;
	permission: NotificationPermission;
};

export type RegisterWebPushOptions = {
	/**
	 * When true, may call `Notification.requestPermission()`.
	 * Browsers (especially Safari) only allow that from a user gesture — never from timers/focus.
	 */
	allowPermissionPrompt?: boolean;
};

/**
 * Register the FCM service worker and return the device token when permission is granted.
 * Does not prompt unless `allowPermissionPrompt` is true (must be from a user gesture).
 */
export async function registerWebPushToken(
	options: RegisterWebPushOptions = {},
): Promise<WebPushRegistrationResult> {
	if (typeof window === "undefined" || !isWebPushConfigured()) {
		return { token: null, permission: Notification.permission };
	}

	if (!("Notification" in window)) {
		return { token: null, permission: "denied" };
	}

	let permission = Notification.permission;
	if (permission === "default") {
		if (!options.allowPermissionPrompt) {
			return { token: null, permission: "default" };
		}
		permission = await Notification.requestPermission();
	}

	if (permission !== "granted") {
		return { token: null, permission };
	}

	const vapidKey = getFirebaseVapidKey();
	const messaging = await getMessagingInstance();
	const registration = await registerServiceWorker();

	if (!vapidKey || !messaging || !registration) {
		return { token: null, permission };
	}

	await navigator.serviceWorker.ready;

	const token = await getToken(messaging, {
		vapidKey,
		serviceWorkerRegistration: registration,
	});

	if (token) {
		sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
	}

	return { token: token || null, permission };
}

export function getStoredWebPushToken(): string | null {
	if (typeof window === "undefined") return null;
	return sessionStorage.getItem(TOKEN_STORAGE_KEY);
}

export function clearStoredWebPushToken(): void {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

export type ForegroundPushPayload = {
	title?: string;
	body?: string;
	redirectPath?: string;
};

export async function subscribeToForegroundPush(
	onPayload: (payload: ForegroundPushPayload) => void,
): Promise<(() => void) | null> {
	const messaging = await getMessagingInstance();
	if (!messaging) return null;

	return onMessage(messaging, (payload) => {
		onPayload({
			title: payload.notification?.title,
			body: payload.notification?.body,
			redirectPath:
				typeof payload.data?.redirectPath === "string"
					? payload.data.redirectPath
					: undefined,
		});
	});
}
